import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Save, UploadCloud, PlugZap, Building2 } from "lucide-react";
import { toast } from "sonner";
import { TransportRates } from "@/components/TransportRates";
import { XeroSettings } from "@/components/XeroSettings";
import { PricebookManager } from "@/components/PricebookManager";

export default function AccountsSettings() {
  const [pt, setPt] = useState(null);
  const [savingPt, setSavingPt] = useState(false);
  const [xero, setXero] = useState({ connected: false, tenant_name: "", account_code_set: false });
  const [showXero, setShowXero] = useState(false);
  const [provider, setProvider] = useState(null);
  const [savingProvider, setSavingProvider] = useState(false);

  const loadXero = () => api.xeroStatus().then(setXero).catch(() => {});
  useEffect(() => {
    api.ruleConfig().then((c) => setPt({
      provider_travel_enabled: c.provider_travel_enabled !== false,
      provider_travel_units: c.provider_travel_units ?? 15,
      provider_travel_max_hours: c.provider_travel_max_hours ?? 3,
      provider_travel_min_shifts: c.provider_travel_min_shifts ?? 2,
    })).catch(() => {});
    api.billingSettings().then((s) => setProvider({
      registration_number: s.registration_number || "",
      provider_number: s.provider_number || "",
      abn: s.abn || "",
      acn: s.acn || "",
    })).catch(() => {});
    loadXero();
  }, []);

  const savePt = async () => {
    setSavingPt(true);
    try { await api.saveProviderTravel(pt); toast.success("Provider travel settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSavingPt(false); }
  };

  const saveProvider = async () => {
    setSavingProvider(true);
    try { const s = await api.saveBillingSettings(provider); setProvider(s); toast.success("NDIS provider details saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSavingProvider(false); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="accounts-settings-page">
      <h1 className="text-2xl font-display font-bold text-slate-800">Accounts Settings</h1>
      <p className="text-sm text-slate-500 mt-1">Xero, provider-travel auto-charge, pricebook items &amp; rates, and the FY rate update tool. Managed by Accounts Managers.</p>

      {/* NDIS provider details (used on the NDIA bulk-payment CSV) */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-5" data-testid="provider-details-card">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-slate-400" />
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">NDIS provider details</div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2 mb-3">Your registered provider identifiers. The <span className="font-semibold">Registration / Provider number</span> and <span className="font-semibold">ABN</span> are written into every NDIA bulk-payment CSV — the CSV export is blocked until the registration number is set.</p>
        {!provider ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-slate-600">NDIS Registration number <span className="text-rose-500">*</span></label>
                <Input value={provider.registration_number} onChange={(e) => setProvider((p) => ({ ...p, registration_number: e.target.value }))}
                  placeholder="e.g. 4050001234" className="h-9 text-xs mt-1" data-testid="provider-registration-number" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-600">Provider number <span className="text-slate-400">(overrides registration # on CSV if set)</span></label>
                <Input value={provider.provider_number} onChange={(e) => setProvider((p) => ({ ...p, provider_number: e.target.value }))}
                  placeholder="Optional" className="h-9 text-xs mt-1" data-testid="provider-number" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-600">ABN</label>
                <Input value={provider.abn} onChange={(e) => setProvider((p) => ({ ...p, abn: e.target.value }))}
                  placeholder="Australian Business Number" className="h-9 text-xs mt-1" data-testid="provider-abn" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-600">ACN</label>
                <Input value={provider.acn} onChange={(e) => setProvider((p) => ({ ...p, acn: e.target.value }))}
                  placeholder="Australian Company Number" className="h-9 text-xs mt-1" data-testid="provider-acn" />
              </div>
            </div>
            <Button size="sm" className="h-8 text-xs mt-4" style={{ backgroundColor: "var(--brand)" }} onClick={saveProvider} disabled={savingProvider} data-testid="save-provider-details">
              {savingProvider ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1.5" /> Save provider details</>}
            </Button>
          </>
        )}
      </div>

      {/* Xero */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-5">
        <div className="flex items-center gap-2">
          <PlugZap className="h-4 w-4 text-slate-400" />
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Xero connection &amp; account mappings</div>
          <span className={`ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full ${xero.connected ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="xero-connection-status">
            {xero.connected ? `Connected${xero.tenant_name ? ` · ${xero.tenant_name}` : ""}` : "Not connected"}
          </span>
        </div>
        <p className="text-[11px] text-slate-500 mt-2 mb-3">Connect your Xero organisation and map NDIS billing types to Xero revenue accounts/items so invoices push correctly.</p>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowXero(true)} data-testid="open-xero-settings">
          <UploadCloud className="h-3.5 w-3.5 mr-1.5" /> Manage Xero settings &amp; mappings
        </Button>
      </div>

      {/* Provider travel */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Provider travel (auto-charge)</div>
        <p className="text-[11px] text-slate-500 mb-3">When a worker has 2+ shifts on the same day, each qualifying short shift automatically attracts a fixed provider-travel charge (units @ $1). Split equally across billing groups when split-billing applies. SIL Weekly is excluded.</p>
        {!pt ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
          <>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex gap-2">
                {[["on", true, "Enabled"], ["off", false, "Disabled"]].map(([id, v, lbl]) => (
                  <button key={id} onClick={() => setPt((c) => ({ ...c, provider_travel_enabled: v }))} data-testid={`ptravel-${id}`}
                    className={`text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
                      pt.provider_travel_enabled === v ? "bg-brand text-white border-brand" : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-600">Units (@ $1)</label>
                <Input type="number" value={pt.provider_travel_units} onChange={(e) => setPt((c) => ({ ...c, provider_travel_units: Number(e.target.value) }))}
                  className="h-9 text-xs w-24 mt-1" data-testid="ptravel-units" disabled={!pt.provider_travel_enabled} />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-600">Applies to shifts ≤</label>
                <div className="flex items-center gap-1 mt-1">
                  <Input type="number" step="0.5" value={pt.provider_travel_max_hours} onChange={(e) => setPt((c) => ({ ...c, provider_travel_max_hours: Number(e.target.value) }))}
                    className="h-9 text-xs w-24" data-testid="ptravel-max-hours" disabled={!pt.provider_travel_enabled} />
                  <span className="text-[10px] text-slate-400">hours</span>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-600">Min shifts/day</label>
                <Input type="number" value={pt.provider_travel_min_shifts} onChange={(e) => setPt((c) => ({ ...c, provider_travel_min_shifts: Number(e.target.value) }))}
                  className="h-9 text-xs w-24 mt-1" data-testid="ptravel-min-shifts" disabled={!pt.provider_travel_enabled} />
              </div>
            </div>
            <Button size="sm" className="h-8 text-xs mt-4" style={{ backgroundColor: "var(--brand)" }} onClick={savePt} disabled={savingPt} data-testid="save-provider-travel">
              {savingPt ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1.5" /> Save provider travel</>}
            </Button>
          </>
        )}
      </div>

      {/* Pricebook & Item Prices — replaces the old Transport Rates section */}
      <div className="mt-5">
        <PricebookManager canEdit={true} />
      </div>

      <XeroSettings open={showXero} onOpenChange={setShowXero} onChanged={loadXero} />
    </div>
  );
}
